/**
 * TREATX-11Y-PATIENT-DASHBOARD-HOOKS-01 (2026-06-15)
 *
 * Patient Notifications Center
 *
 * Reads exclusively from the canonical patient feed endpoints:
 *   GET  /api/patient/notifications
 *   GET  /api/patient/notifications/unread-count
 *   POST /api/patient/notifications/:id/mark-read
 *
 * No proxy data. No projection. No second store.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell, ArrowLeft, Loader2, CheckCircle2, RefreshCw, Inbox,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import api from '../../utils/api';

function fmt(iso) {
  if (!iso) return '—';
  try {
    return formatDateTimeLocale(iso);
  } catch {
    return iso;
  }
}

const statusBadgeClasses = (status) => {
  const v = (status || '').toLowerCase();
  if (v === 'sent') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (v.includes('fail')) return 'bg-rose-100 text-rose-700 border-rose-200';
  if (v.includes('block') || v.includes('skip')) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const PatientNotificationsCenter = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (tab === 'unread') params.unread = 'true';
      const [list, cnt] = await Promise.all([
        api.get('/patient/notifications', { params }),
        api.get('/patient/notifications/unread-count'),
      ]);
      setItems(((list.data && list.data.notifications) || []));
      setTotal(((list.data && list.data.total) || 0));
      setUnread(((cnt.data && cnt.data.unread) || 0));
    } catch (e) {
      setError((e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || e.message);
      setItems([]);
      setTotal(0);
      setUnread(0);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/patient/notifications/${id}/mark-read`);
      toast({ title: 'Marked as read' });
      load();
    } catch (e) {
      const msg = (e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || e.message;
      toast({ title: 'Could not mark as read', description: msg, variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-notifications-center">
      <Toaster />

      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/patient/dashboard')}
              data-testid="patient-notif-back"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> {t('adminNav.dashboard')}
            </Button>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <Bell className="h-5 w-5 text-blue-600" />
                {t('patient.notificationsCenter.title')}
              </h1>
              <p className="text-xs text-slate-500">
                {t('patient.notificationsCenter.subtitle')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge data-testid="patient-notif-unread-count" className="bg-blue-100 text-blue-700 border-blue-200">
              {unread} {t('patient.messages.unread').toLowerCase()}
            </Badge>
            <Button variant="outline" size="sm" onClick={load} data-testid="patient-notif-refresh">
              <RefreshCw className="h-4 w-4 mr-1" /> {t('patient.common.refresh')}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-4">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="patient-notif-tabs">
            <TabsTrigger value="all" data-testid="patient-notif-tab-all">
              {t('patient.notificationsCenter.filterAll')} ({total})
            </TabsTrigger>
            <TabsTrigger value="unread" data-testid="patient-notif-tab-unread">
              {t('patient.notificationsCenter.filterUnread')} ({unread})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {tab === 'unread' ? t('patient.notificationsCenter.filterUnread') : t('patient.notificationsCenter.title')}
                </CardTitle>
                <CardDescription>
                  {t('patient.notificationsCenter.subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {loading ? (
                  <div className="py-8 flex items-center justify-center text-slate-500" data-testid="patient-notif-loading">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t('patient.common.loading')}
                  </div>
                ) : error ? (
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="patient-notif-error">
                    {error}
                  </div>
                ) : items.length === 0 ? (
                  <div className="py-10 text-center text-slate-500" data-testid="patient-notif-empty">
                    <Inbox className="h-7 w-7 mx-auto mb-2 text-slate-300" />
                    <div className="text-sm">{t('patient.notificationsCenter.empty')}</div>
                  </div>
                ) : (
                  <ul className="divide-y" data-testid="patient-notif-list">
                    {items.map((n) => (
                      <li
                        key={n.id}
                        className={`flex items-start gap-3 py-3 ${n.read ? 'opacity-70' : ''}`}
                        data-testid={`patient-notif-item-${n.id}`}
                      >
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-1 ${n.read ? 'bg-slate-100' : 'bg-blue-100'}`}>
                          <Bell className={`h-4 w-4 ${n.read ? 'text-slate-400' : 'text-blue-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm ${n.read ? 'text-slate-600' : 'text-slate-900 font-medium'} truncate`}>
                              {n.subject || n.event || t('patient.dashboard.notifications')}
                            </p>
                            {n.event && (
                              <Badge variant="outline" className="text-[10px]">{n.event}</Badge>
                            )}
                            {n.status && (
                              <Badge className={statusBadgeClasses(n.status) + ' text-[10px]'}>
                                {String(n.status).toLowerCase()}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{fmt(n.created_at)}</p>
                          {n.template_source && (
                            <p className="text-[11px] text-slate-400 mt-0.5">via {n.template_source}{n.locale ? ` · ${n.locale}` : ''}</p>
                          )}
                        </div>
                        {!n.read && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markRead(n.id)}
                            data-testid={`patient-notif-mark-read-${n.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" /> {t('patient.notifications.markRead')}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <p className="text-[11px] text-slate-400 text-center" data-testid="patient-notif-canonical-note">
          Source of truth: canonical <code>notifications_log</code> via <code>NotificationService</code>. No projection or cache.
        </p>
      </div>
    </div>
  );
};

export default PatientNotificationsCenter;
